import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link, useSearchParams } from "react-router-dom";
import { useAtomValue } from "jotai";
import { ROUTES } from "@routing/routes";
import { Button, ErrorState, Spinner, Title } from "@common/index";
import { ComponentColor } from "@common/types";
import { useAuth } from "@/hooks/useAuth";
import { signInRedirect } from "@components/Auth/signInRedirect";
import { useInvitationApi } from "@/hooks/apiHooks/useInvitationApi";
import type { ValidateInvitationResponse } from "@/hooks/apiHooks/useInvitationApi";
import { isProcessingInvitationAtom } from "@/store/invitationAtoms";
import { completeInvitationOutcome } from "./_helpers/completeInvitationOutcome";
import { validateInvitationOutcome } from "./_helpers/validateInvitationOutcome";
import { RequestNewInviteLink } from "./_components/RequestNewInviteLink";

// Entra External ID enforces the real policy server-side; a violation comes back
// as an error from the complete-signup POST. This client rule is a friendly
// pre-check that matches the common Entra default (8+ chars, mixed case + a
// number). TODO(backend): confirm the tenant's configured password policy and
// tighten this to match if it differs.
const PASSWORD_COMPLEXITY = /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/;

const passwordSchema = z
    .object({
        password: z
            .string()
            .min(8, "Password must be at least 8 characters")
            .regex(
                PASSWORD_COMPLEXITY,
                "Include an uppercase letter, a lowercase letter, and a number",
            ),
        confirmPassword: z.string().nonempty("Please confirm your password"),
    })
    .refine((data) => data.password === data.confirmPassword, {
        message: "Passwords do not match",
        path: ["confirmPassword"],
    });

type PasswordFormData = z.infer<typeof passwordSchema>;

// The invitation is either being checked, valid (show the form), invalid (bad/
// missing/expired token), failed (the check itself could not answer), already
// completed (redirecting to sign in), or refused because the email already has
// an account (existing-account).
type Status =
    | "validating"
    | "valid"
    | "invalid"
    | "failed"
    | "redirecting"
    | "existing-account";

const GENERIC_FAILURE =
    "We couldn't finish creating your account. Please try again in a moment.";

export const InviteLandingPage = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const { instance } = useAuth();
    const { validateInvitation, completeInvitation } = useInvitationApi();
    const isProcessing = useAtomValue(isProcessingInvitationAtom);

    // A missing token is invalid up front, with no request to fire — derived
    // here rather than in the effect so no state is set synchronously on mount.
    const [status, setStatus] = useState<Status>(
        token ? "validating" : "invalid",
    );
    const [invite, setInvite] = useState<ValidateInvitationResponse | null>(
        null,
    );
    const [submitError, setSubmitError] = useState<string | null>(null);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<PasswordFormData>({
        resolver: zodResolver(passwordSchema),
    });

    const runValidation = useCallback(() => {
        // A missing token can't be looked up, and firing a request for "" would
        // only leak that the endpoint exists — the initial "invalid" status
        // already covers it, so there is nothing to do here.
        if (!token) {
            return;
        }

        // setState happens only in the async callbacks, never synchronously in
        // the effect body (react-hooks/set-state-in-effect).
        validateInvitation(token)
            .then((info) => {
                setInvite(info);
                setStatus("valid");
            })
            .catch((error: unknown) =>
                // The validate endpoint answers an unknown, expired or consumed
                // token with a 404, and any other refusal of this link (a 4xx)
                // would repeat on every Retry — both are "invalid". A check
                // that could not answer (a 5xx, a timeout or rate limit, a
                // dropped network) says nothing about the link, and calling it
                // invalid would have a good invitation replaced by a new link.
                setStatus(validateInvitationOutcome(error)),
            );
    }, [token, validateInvitation]);

    // Back to the verifying state from the handler (not the effect), then re-ask.
    const retryValidation = () => {
        setStatus("validating");
        runValidation();
    };

    useEffect(() => {
        runValidation();
    }, [runValidation]);

    const handleSignIn = () => signInRedirect(instance);

    const onSubmit = async (data: PasswordFormData) => {
        // The form only renders with a validated token, so this is defensive.
        if (!token) {
            return;
        }

        setSubmitError(null);

        try {
            await completeInvitation(token, data.password);

            // The account now exists; MSAL takes over. redirectStartPage is
            // /c/browse by default — a brand-new (unapproved) user is bounced to
            // the pending wall by the ApprovalGate, which is the right landing.
            setStatus("redirecting");
            signInRedirect(instance);
        } catch (error) {
            switch (completeInvitationOutcome(error)) {
                case "invalid-token": {
                    // The token expired/was consumed between load and submit.
                    setStatus("invalid");
                    break;
                }

                case "already-registered": {
                    setStatus("existing-account");
                    break;
                }

                default: {
                    setSubmitError(GENERIC_FAILURE);
                    break;
                }
            }
        }
    };

    if (status === "validating") {
        return (
            <div className="max-w-md mx-auto px-4 py-16 text-primary text-center">
                <Spinner size="lg" />
                <p className="mt-4 text-base">Verifying your invitation…</p>
            </div>
        );
    }

    if (status === "invalid") {
        return (
            <div className="max-w-md mx-auto px-4 py-10 text-primary">
                {/* No token at all is a way in, not a dead link: the approval
                    email for an applicant who hasn't signed up links here
                    without one (PR 327 round 5). */}
                {token ? (
                    <>
                        <Title>This link isn&apos;t valid</Title>
                        <p className="text-base mt-6">
                            This invitation link is invalid or has expired.
                            Enter the email you applied with and we&apos;ll send
                            you a new one.
                        </p>
                    </>
                ) : (
                    <>
                        <Title>Finish setting up your account</Title>
                        <p className="text-base mt-6">
                            Use the signup link we emailed you, or enter the
                            email you applied with and we&apos;ll send you a new
                            one.
                        </p>
                    </>
                )}
                <RequestNewInviteLink />
                {/* #438: re-applying is not the way back for someone who
                    already applied, so it comes second. */}
                <p className="mt-6 text-base">
                    Haven&apos;t applied yet?{" "}
                    <Link to={ROUTES.APPLY} className="link link-primary font-bold">
                        Apply for an account
                    </Link>
                </p>
            </div>
        );
    }

    if (status === "failed") {
        return (
            <div className="max-w-md mx-auto px-4 py-10">
                <ErrorState
                    message="Couldn't verify your invitation."
                    onRetry={retryValidation}
                />
            </div>
        );
    }

    // Audit OB-03 (ruling 2026-10-20): usually someone who already has an
    // account applied for another business. One user per organization at launch
    // (OB-07), so we set the second one up by hand. But the account can also be
    // this applicant's own: a submit that created it and whose response was
    // lost (review of PR 353). Sign In is the way in for that case, offered
    // second. No form: it would only get the same 409 again.
    if (status === "existing-account") {
        return (
            <div className="max-w-md mx-auto px-4 py-10 text-primary">
                <Title>You already have an account</Title>
                <p className="text-base mt-6">
                    This email already has an account with us. To add another
                    business, contact us and we&apos;ll set it up.
                </p>
                <div className="mt-8">
                    <Button
                        to={ROUTES.SUPPORT}
                        color={ComponentColor.Primary}
                        className="normal-case"
                    >
                        Contact us
                    </Button>
                </div>
                <p className="mt-6 text-base">
                    If this is your account,{" "}
                    <button
                        type="button"
                        onClick={handleSignIn}
                        className="link link-primary font-bold normal-case"
                    >
                        sign in
                    </button>
                    .
                </p>
            </div>
        );
    }

    if (status === "redirecting") {
        return (
            <div className="max-w-md mx-auto px-4 py-16 text-primary text-center">
                <Spinner size="lg" />
                <p className="mt-4 text-base">
                    Your account is ready — taking you to sign in…
                </p>
            </div>
        );
    }

    return (
        <div className="max-w-md mx-auto px-4 py-10 text-primary">
            <Title>Set your password</Title>

            <p className="text-base mt-6">
                Welcome, <span className="font-bold">{invite?.organizationName}</span>.
                Choose a password for <span className="font-bold">{invite?.email}</span>{" "}
                to finish creating your account.
            </p>

            <form
                onSubmit={handleSubmit(onSubmit)}
                noValidate
                className="mt-8 rounded-2xl bg-base-200 p-6 sm:p-8 space-y-6"
            >
                <div>
                    <label htmlFor="password" className="block font-bold mb-2">
                        Password
                    </label>
                    <input
                        id="password"
                        type="password"
                        autoComplete="new-password"
                        className="input input-bordered w-full bg-white text-neutral"
                        {...register("password")}
                    />
                    <p className="text-xs text-neutral/60 mt-1">
                        At least 8 characters, with an uppercase letter, a
                        lowercase letter, and a number.
                    </p>
                    {errors.password && (
                        <p className="text-red-500 text-sm mt-1">
                            {errors.password.message}
                        </p>
                    )}
                </div>

                <div>
                    <label
                        htmlFor="confirmPassword"
                        className="block font-bold mb-2"
                    >
                        Confirm Password
                    </label>
                    <input
                        id="confirmPassword"
                        type="password"
                        autoComplete="new-password"
                        className="input input-bordered w-full bg-white text-neutral"
                        {...register("confirmPassword")}
                    />
                    {errors.confirmPassword && (
                        <p className="text-red-500 text-sm mt-1">
                            {errors.confirmPassword.message}
                        </p>
                    )}
                </div>

                {submitError && (
                    <div role="alert" className="alert alert-error">
                        <span>{submitError}</span>
                    </div>
                )}

                <Button
                    type="submit"
                    color={ComponentColor.Warning}
                    loading={isProcessing}
                    className="text-neutral font-bold normal-case"
                >
                    Create Account
                </Button>
            </form>

            <p className="mt-6 text-base">
                Already have an account?{" "}
                <button
                    type="button"
                    id="invite-signin-button"
                    onClick={handleSignIn}
                    className="link link-primary font-bold normal-case"
                >
                    Sign In
                </button>
            </p>
        </div>
    );
};
